import React, { useState } from 'react';
import '../styles/Services.css';
import '../styles/responsive.css';
import { FaEdit, FaTrash, FaSearch, FaCalendarAlt, FaPlus } from 'react-icons/fa';
import { BsTruck, BsBoxSeam, BsBuilding, BsPerson } from 'react-icons/bs';
import { useServices, useServiceCategories } from '../hooks/useServices';
import { useSearch } from '../contexts/SearchContext';
import ResponsiveTable from '../components/ResponsiveTable';
import CreateServiceModal from '../components/modals/CreateServiceModal';
import DuplicateServiceModal from '../components/modals/DuplicateServiceModal';
import DeleteServiceModal from '../components/modals/DeleteServiceModal';
import DeactivateServiceModal from '../components/modals/DeactivateServiceModal';

const Services = () => {
  const { services, stats, loading, error, refetch, duplicateService, toggleServiceStatus, deleteService } = useServices();
  const { categories } = useServiceCategories();
  const { globalSearch } = useSearch();

  const [selectedServices, setSelectedServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // États des modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Icônes pour les services
  const getServiceIcon = (serviceName) => {
    switch (serviceName?.toLowerCase()) {
      case 'transport':
        return <BsTruck />;
      case 'livraison':
        return <BsBoxSeam />;
      case 'location btp':
      case 'location':
        return <BsBuilding />;
      case 'conducteur':
        return <BsPerson />;
      default:
        return <BsTruck />;
    }
  };

  // Gestion de la sélection des services
  const handleServiceSelection = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedServices(checked ? services.map(s => s.id) : []);
  };

  // Actions sur les services
  const handleDuplicate = (service) => {
    setSelectedService(service);
    setShowDuplicateModal(true);
  };

  const handleDeactivate = (service) => {
    setSelectedService(service);
    setShowDeactivateModal(true);
  };

  const handleDelete = (service) => {
    setSelectedService(service);
    setShowDeleteModal(true);
  };

  const handleEdit = (service) => {
    // Navigation vers la page de modification
    console.log('Éditer le service:', service);
  };

  // Désactivation en lot
  const handleBulkDeactivate = async () => {
    try {
      for (const serviceId of selectedServices) {
        await toggleServiceStatus(serviceId, false);
      }
      setSelectedServices([]);
      refetch();
    } catch (error) {
      console.error('Erreur lors de la désactivation en lot:', error);
    }
  };

  // Filtrage des services
  const filteredServices = services.filter(service => {
    // Filtrage par recherche globale (du header)
    const matchesGlobalSearch = !globalSearch || 
      service.name.toLowerCase().includes(globalSearch.toLowerCase()) ||
      service.description?.toLowerCase().includes(globalSearch.toLowerCase());
    
    // Filtrage par recherche locale (de la page)
    const matchesLocalSearch = searchTerm === '' || 
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = dateFilter === '' || 
      service.created_at === dateFilter;
    
    return matchesGlobalSearch && matchesLocalSearch && matchesDate;
  });

  if (loading) {
    return <div className="loading">Chargement des services...</div>;
  }

  // Configuration pour le tableau responsive
  const columns = [
    {
      header: 'Id',
      accessor: 'id'
    },
    {
      header: 'Nom service',
      accessor: 'name'
    },
    {
      header: 'Icon service',
      accessor: 'icon',
      render: (service) => <div className="icon">{getServiceIcon(service.name)}</div>
    },
    {
      header: 'Commission',
      accessor: 'commission'
    },
    {
      header: 'Date de création',
      accessor: 'created_at',
      render: (service) => new Date(service.created_at).toLocaleDateString('fr-FR')
    },
    {
      header: 'Statut',
      accessor: 'status',
      render: (service) => (
        <span className={service.status === 'Activé' ? 'status-green' : 'status-red'}>
          {service.status}
        </span>
      )
    }
  ];

  const mobileCardConfig = {
    title: (service) => service.name,
    subtitle: (service) => `ID: ${service.id}`,
    fields: [
      { label: 'Commission', value: (service) => service.commission },
      { label: 'Date', value: (service) => new Date(service.created_at).toLocaleDateString('fr-FR') },
      { label: 'Statut', value: (service) => service.status }
    ],
    actions: [
      {
        label: 'Dupliqué',
        onClick: handleDuplicate,
        className: 'action-link'
      },
      {
        label: 'Désactiver',
        onClick: handleDeactivate,
        className: 'action-red'
      },
      {
        icon: <FaEdit />,
        onClick: handleEdit,
        className: 'icon-action'
      },
      {
        icon: <FaTrash />,
        onClick: handleDelete,
        className: 'icon-action'
      }
    ]
  };

  return (
    <div className="services-container">
      <h2>Services</h2>

      {/* Section des statistiques - Layout Figma */}
      <div className="stats-section-figma">
        <div className="stats-row">
          <div className="stats-card-small">
            <div className="label">Nbr de services actifs</div>
            <div className="value blue">{stats.active_services || services.filter(s => s.status === 'Activé' || s.is_active).length}</div>
          </div>

          <div className="stats-card-large">
            <div className="title">Classement des services selon le nbr de prestataire</div>
            <div className="services-grid">
              {(stats.service_ranking && stats.service_ranking.length > 0
                ? stats.service_ranking.slice(0, 4)
                : services.slice(0, 4)
              ).map((service, idx) => (
                <div key={service.id || idx} className="service-card">
                  <div className="service-name">{service.name || service.service_name || 'Service'}</div>
                  <div className="service-details">
                    <div className="stat-line">Taux d'activation : {service.activation_rate || service.prestataires_count || 0}</div>
                    <div className="stat-line">Taux de rejet : {service.rejection_rate || 0}</div>
                  </div>
                </div>
              ))}
              {services.length === 0 && !stats.service_ranking?.length && (
                <div className="service-card">
                  <div className="service-name">Aucun service</div>
                  <div className="service-details">
                    <div className="stat-line">Pas de données disponibles</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="stats-row">
          <div className="stats-card-large numbered">
            <div className="title">Classement des services selon le nbr de prestataire</div>
            <div className="services-grid-numbered">
              {(stats.service_ranking && stats.service_ranking.length > 0
                ? stats.service_ranking.slice(0, 4)
                : services.slice(0, 4)
              ).map((service, idx) => {
                const colorClasses = ['transport', 'livraison', 'location', 'conducteur'];
                return (
                  <div key={service.id || idx} className="service-number-card">
                    <div className={`service-name ${colorClasses[idx] || ''}`}>{service.name || service.service_name || 'Service'}</div>
                    <div className={`big-number ${colorClasses[idx] || ''}`}>{service.prestataires_count || service.count || 0}</div>
                  </div>
                );
              })}
              {services.length === 0 && !stats.service_ranking?.length && (
                <div className="service-number-card">
                  <div className="service-name">--</div>
                  <div className="big-number">0</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Barre de recherche et actions */}
      <div className="search-actions">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Recherche..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="date-field">
          <label>Date du transaction</label>
          <div className="date-input">
            <FaCalendarAlt />
            <input 
              type="date" 
              placeholder="jj/mm/aaaa"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>
        </div>

        <button 
          className="btn-secondary"
          disabled={selectedServices.length === 0}
          onClick={handleBulkDeactivate}
        >
          Désactiver
        </button>
        
        <button 
          className="btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus /> Créer un service
        </button>
      </div>

      {/* Tableau responsive des services */}
      <ResponsiveTable
        data={filteredServices}
        columns={columns}
        mobileCardConfig={mobileCardConfig}
        onSelectionChange={setSelectedServices}
        selectedItems={selectedServices}
        onSelectAll={handleSelectAll}
      />

      {error && (
        <div className="error-message">
          Erreur lors du chargement : {error}
        </div>
      )}

      {/* Modales */}
      {showCreateModal && (
        <CreateServiceModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
          categories={categories}
        />
      )}

      {showDuplicateModal && selectedService && (
        <DuplicateServiceModal
          service={selectedService}
          onClose={() => {
            setShowDuplicateModal(false);
            setSelectedService(null);
          }}
          onSuccess={() => {
            setShowDuplicateModal(false);
            setSelectedService(null);
            refetch();
          }}
        />
      )}

      {showDeleteModal && selectedService && (
        <DeleteServiceModal
          service={selectedService}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedService(null);
          }}
          onSuccess={() => {
            setShowDeleteModal(false);
            setSelectedService(null);
            refetch();
          }}
        />
      )}

      {showDeactivateModal && selectedService && (
        <DeactivateServiceModal
          service={selectedService}
          onClose={() => {
            setShowDeactivateModal(false);
            setSelectedService(null);
          }}
          onSuccess={() => {
            setShowDeactivateModal(false);
            setSelectedService(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default Services;